import React, { useState, useEffect, useRef } from 'react';

const SUBTITLE = 'Software Development Engineer in Test';
const BADGES = ['14+ Years IT', 'SDET', 'AI-Driven Testing', 'MERN Stack', 'Mobile & Web'];

const MODES = [
  {
    name: 'Pentagon',
    freq: '440 Hz', freqHz: 440,
    note: 'La',    sources: 5,
    desc: 'Concert pitch — the universal tuning reference',
    K: 24, color: [0, 212, 255],
    orbits: Array.from({ length: 5 }, (_, i) => ({
      r: 0.30, sx: 0.075, sy: 0.055, phase: (i / 5) * Math.PI * 2,
    })),
  },
  {
    name: 'Hexagon',
    freq: '528 Hz', freqHz: 528,
    note: 'Do',    sources: 6,
    desc: 'Solfeggio tone — traditionally called the "transformation" tone',
    K: 34, color: [57, 211, 83],
    orbits: Array.from({ length: 6 }, (_, i) => ({
      r: 0.28, sx: 0.058, sy: 0.058, phase: (i / 6) * Math.PI * 2,
    })),
  },
  {
    name: 'Dense',
    freq: '741 Hz', freqHz: 741,
    note: 'Fa♯',   sources: 9,
    desc: 'Solfeggio tone — traditionally linked to expression',
    K: 44, color: [163, 113, 247],
    orbits: Array.from({ length: 9 }, (_, i) => ({
      r: 0.18 + (i % 3) * 0.08,
      sx: 0.068 + (i % 4) * 0.018,
      sy: 0.052 + (i % 3) * 0.016,
      phase: (i / 9) * Math.PI * 2,
    })),
  },
  {
    name: 'Spiral',
    freq: '396 Hz', freqHz: 396,
    note: 'Sol',   sources: 6,
    desc: 'Solfeggio tone — traditionally called the "liberation" tone',
    K: 18, color: [255, 160, 50],
    orbits: [
      { r: 0.10, sx: 0.17, sy: 0.11, phase: 0 },
      { r: 0.18, sx: 0.13, sy: 0.09, phase: Math.PI * 0.5 },
      { r: 0.26, sx: 0.10, sy: 0.07, phase: Math.PI * 1.0 },
      { r: 0.34, sx: 0.08, sy: 0.055, phase: Math.PI * 1.5 },
      { r: 0.22, sx: 0.14, sy: 0.10, phase: Math.PI * 0.25 },
      { r: 0.14, sx: 0.20, sy: 0.13, phase: Math.PI * 1.75 },
    ],
  },
  {
    name: 'Star',
    freq: '963 Hz', freqHz: 963,
    note: 'Si',    sources: 7,
    desc: 'Solfeggio tone — the highest of the set, near a Si',
    K: 38, color: [0, 230, 200],
    orbits: [
      ...Array.from({ length: 3 }, (_, i) => ({
        r: 0.32, sx: 0.062, sy: 0.048, phase: (i / 3) * Math.PI * 2,
      })),
      ...Array.from({ length: 3 }, (_, i) => ({
        r: 0.18, sx: 0.095, sy: 0.075, phase: (i / 3) * Math.PI * 2 + Math.PI / 3,
      })),
      { r: 0.08, sx: 0.12, sy: 0.09, phase: 0 },
    ],
  },
  {
    name: 'Noise',
    freq: '174 Hz', freqHz: 174,
    note: 'Fa',    sources: 11,
    desc: 'Solfeggio tone — the lowest of the set',
    K: 14, color: [200, 80, 255],
    orbits: Array.from({ length: 11 }, (_, i) => ({
      r: 0.10 + (i % 5) * 0.045,
      sx: 0.04 + (i % 7) * 0.015,
      sy: 0.03 + (i % 6) * 0.014,
      phase: (i * 1.618) % (Math.PI * 2),
    })),
  },
];

const MODE_DURATION = 9;
const TRANSITION    = 2.5;
const lerp3 = (a, b, t) => a.map((v, i) => v + (b[i] - v) * t);

const Header = ({ darkMode, toggleTheme }) => {
  const [displayText, setDisplayText] = useState('');
  const [modeIndex, setModeIndex]     = useState(0);
  const [playing, setPlaying]         = useState(false);
  const [infoOpen, setInfoOpen]       = useState(false);
  const canvasRef  = useRef(null);
  const infoRef    = useRef(null);
  const modeIdxRef = useRef(0);
  const audioCtxRef = useRef(null);
  const nodesRef    = useRef(null);

  // ── Audio helpers ────────────────────────────────────────────────────────
  const stopNodes = (fadeMs = 400) => {
    if (!nodesRef.current || !audioCtxRef.current) return;
    const { masterGain, osc1, osc2, lfo } = nodesRef.current;
    const ctx = audioCtxRef.current;
    const now = ctx.currentTime;
    masterGain.gain.cancelScheduledValues(now);
    masterGain.gain.setValueAtTime(masterGain.gain.value, now);
    masterGain.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
    setTimeout(() => {
      try { osc1.stop(); osc2.stop(); lfo.stop(); } catch (_) {}
      nodesRef.current = null;
    }, fadeMs + 50);
  };

  const startAudio = (freqHz) => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!audioCtxRef.current) audioCtxRef.current = new AC();
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    stopNodes(200);

    setTimeout(() => {
      if (!audioCtxRef.current) return;
      const now = ctx.currentTime;

      // Master gain — gentle fade-in
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0, now);
      masterGain.gain.linearRampToValueAtTime(0.07, now + 0.5);
      masterGain.connect(ctx.destination);

      // Osc 1: fundamental sine — main tone
      const osc1 = ctx.createOscillator();
      const g1   = ctx.createGain();
      g1.gain.value = 0.75;
      osc1.type = 'sine';
      osc1.frequency.value = freqHz;
      osc1.connect(g1); g1.connect(masterGain);

      // Osc 2: octave up at low volume — adds warmth like a singing bowl
      const osc2 = ctx.createOscillator();
      const g2   = ctx.createGain();
      g2.gain.value = 0.18;
      osc2.type = 'sine';
      osc2.frequency.value = freqHz * 2;
      osc2.connect(g2); g2.connect(masterGain);

      // LFO: very slow tremolo (0.4 Hz) — makes it feel alive, not static
      const lfo     = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.frequency.value = 0.4;
      lfoGain.gain.value  = 0.012;
      lfo.connect(lfoGain);
      lfoGain.connect(masterGain.gain);

      osc1.start(); osc2.start(); lfo.start();
      nodesRef.current = { masterGain, osc1, osc2, lfo };
    }, 250);
  };

  const toggleAudio = () => {
    if (playing) {
      stopNodes(500);
      setPlaying(false);
    } else {
      startAudio(MODES[modeIdxRef.current].freqHz);
      setPlaying(true);
    }
  };

  // Update frequency smoothly when mode changes while audio is on
  useEffect(() => {
    if (playing && nodesRef.current && audioCtxRef.current) {
      const ctx  = audioCtxRef.current;
      const freq = MODES[modeIndex].freqHz;
      const now  = ctx.currentTime;
      nodesRef.current.osc1.frequency.linearRampToValueAtTime(freq,     now + 1.2);
      nodesRef.current.osc2.frequency.linearRampToValueAtTime(freq * 2, now + 1.2);
    }
  }, [modeIndex, playing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopNodes(100);
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Typing
  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      setDisplayText(SUBTITLE.slice(0, i + 1));
      i++;
      if (i === SUBTITLE.length) clearInterval(timer);
    }, 50);
    return () => clearInterval(timer);
  }, []);

  // Cymatics canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let W = 0, H = 0, dpr = 1, animId;
    let time = 0, modeTime = 0;

    const off = document.createElement('canvas');
    const offCtx = off.getContext('2d');

    // Adaptive scale: smaller viewports need finer off-screen resolution
    const getScale = (w) => w < 480 ? 2 : w < 900 ? 3 : 4;

    const resize = () => {
      // Cap dpr at 2 — 3x adds 9× pixels with minimal visual gain on this effect
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W   = canvas.offsetWidth;
      H   = canvas.offsetHeight;
      // Physical pixels → crisp on Retina / high-DPI mobile
      canvas.width  = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const getSources = (mode, t) =>
      mode.orbits.map(o => ({
        x: 0.5 + Math.cos(o.phase + t * o.sx) * o.r,
        y: 0.5 + Math.sin(o.phase + t * o.sy) * o.r,
      }));

    const getAmp = (x, y, sources, K, aspect) => {
      let amp = 0;
      for (let s = 0; s < sources.length; s++) {
        const dx = (x - sources[s].x) * aspect;
        const dy = y - sources[s].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        amp += Math.sin(K * dist - time * 1.8) * Math.exp(-dist * 2.2);
      }
      return amp / sources.length;
    };

    const draw = () => {
      const dt = 0.012;
      time     += dt;
      modeTime += dt;

      const cyclePos   = modeTime % MODE_DURATION;
      const blend      = Math.min(1, Math.max(0, cyclePos - (MODE_DURATION - TRANSITION)) / TRANSITION);

      if (modeTime >= MODE_DURATION) {
        modeIdxRef.current = (modeIdxRef.current + 1) % MODES.length;
        setModeIndex(modeIdxRef.current);
        modeTime = 0;
      }

      const isTransitioning = blend > 0.05 && blend < 0.95;
      infoRef.current?.classList.toggle('transitioning', isTransitioning);

      const modeA = MODES[modeIdxRef.current];
      const modeB = MODES[(modeIdxRef.current + 1) % MODES.length];

      const SCALE = getScale(W);
      // Off-screen buffer at CSS-pixel resolution / SCALE — drawn to physical pixels
      const sw = Math.max(1, Math.ceil(W / SCALE));
      const sh = Math.max(1, Math.ceil(H / SCALE));
      if (off.width !== sw || off.height !== sh) {
        off.width = sw; off.height = sh;
      }

      const imageData = offCtx.createImageData(sw, sh);
      const data = imageData.data;
      const aspect = W / H;

      const srcA = getSources(modeA, time);
      const srcB = getSources(modeB, time);
      const col  = blend > 0 ? lerp3(modeA.color, modeB.color, blend) : modeA.color;
      const [cr, cg, cb] = col;
      const isDark = !document.body.classList.contains('light');

      for (let py = 0; py < sh; py++) {
        for (let px = 0; px < sw; px++) {
          const x = px / sw;
          const y = py / sh;

          const ampA = getAmp(x, y, srcA, modeA.K, aspect);
          const ampB = blend > 0 ? getAmp(x, y, srcB, modeB.K, aspect) : ampA;
          const amp  = ampA * (1 - blend) + ampB * blend;

          const nodal  = Math.max(0, 1 - Math.abs(amp) * 3.8);
          const bright = nodal * nodal * nodal * nodal;

          const idx = (py * sw + px) * 4;
          if (isDark) {
            data[idx]     = Math.floor(bright * cr * 0.12);
            data[idx + 1] = Math.floor(bright * cg * 0.95);
            data[idx + 2] = Math.floor(bright * cb);
            data[idx + 3] = Math.floor(bright * 185 + 4);
          } else {
            data[idx]     = Math.floor(bright * cr * 0.25);
            data[idx + 1] = Math.floor(bright * cg * 0.40);
            data[idx + 2] = Math.floor(bright * cb * 0.95);
            data[idx + 3] = Math.floor(bright * 140 + 4);
          }
        }
      }

      offCtx.putImageData(imageData, 0, 0);
      // Draw to full physical pixel canvas → browser never re-scales it blurrily
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(off, 0, 0, canvas.width, canvas.height);

      animId = requestAnimationFrame(draw);
    };

    // Pause the loop while the hero is off-screen or the tab is hidden
    let inView = true;
    const sync = () => {
      const shouldRun = inView && !document.hidden;
      if (shouldRun && !animId) draw();
      else if (!shouldRun && animId) { cancelAnimationFrame(animId); animId = 0; }
    };
    const io = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([e]) => { inView = e.isIntersecting; sync(); })
      : null;
    if (io) io.observe(canvas);
    document.addEventListener('visibilitychange', sync);
    draw();

    return () => {
      cancelAnimationFrame(animId);
      animId = 0;
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const mode = MODES[modeIndex];
  const [r, g, b] = mode.color;
  const accentColor = `rgb(${r},${g},${b})`;

  return (
    <section className="hero-section">
      <canvas ref={canvasRef} className="hero-canvas" aria-hidden="true" />

      {/* Mode info pill — bottom of hero, above canvas */}
      <div ref={infoRef} className="cymatics-info">
        <button
          className={`cymatics-audio-btn${playing ? ' active' : ''}`}
          onClick={toggleAudio}
          title={playing ? 'Stop frequency' : `Listen to ${mode.freq}`}
          style={{ '--accent': accentColor }}
          aria-label={playing ? 'Stop audio' : 'Play frequency tone'}
          aria-pressed={playing}
        >
          {playing ? '◼' : '♪'}
        </button>
        <span className="cymatics-dot" style={{ background: accentColor, boxShadow: `0 0 8px ${accentColor}` }} />
        <span className="cymatics-name" style={{ color: accentColor }}>{mode.name}</span>
        <span className="cymatics-sep">·</span>
        <span className="cymatics-freq">{mode.freq}</span>
        <span className="cymatics-note">({mode.note})</span>
        <span className="cymatics-sep">·</span>
        <span className="cymatics-sources">{mode.sources} sources</span>
        <span className="cymatics-sep cymatics-sep-lg">—</span>
        <span className="cymatics-desc">{mode.desc}</span>
        <button
          className="cymatics-help-btn"
          onClick={() => setInfoOpen(o => !o)}
          onMouseEnter={() => setInfoOpen(true)}
          onMouseLeave={() => setInfoOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setInfoOpen(false)}
          aria-expanded={infoOpen}
          aria-controls="cymatics-tip"
          aria-label="Why these frequencies?"
        >
          ?
        </button>
      </div>

      <div
        id="cymatics-tip"
        role="tooltip"
        className={`cymatics-tip${infoOpen ? ' open' : ''}`}
        style={{ '--accent': accentColor }}
      >
        <strong>Why these frequencies?</strong>
        <p>
          <b>Cymatics</b> is sound made visible. Each pattern comes from several moving
          wave sources whose ripples overlap and interfere. A different frequency changes
          the wavelength, so the whole pattern changes.
        </p>
        <p>
          <b>440 Hz</b> is the standard concert pitch (La) used to tune instruments. The
          others come from the <b>Solfeggio scale</b>, tones that tradition links to
          wellness. That part is folklore, not proven science. I picked them because each
          one looks and sounds distinct.
        </p>
        <p className="cymatics-tip-hint">Press ♪ to hear the current tone.</p>
      </div>

      <div className="hero-inner">
        <div className="profile-wrapper">
          <img src="/icons/profile.png" alt="Cesar Ramirez" className="profile-image" />
        </div>
        <div className="hero-text">
          <h1>Cesar Ramirez</h1>
          <p className="hero-subtitle">
            {displayText}<span className="cursor" />
          </p>
          <p className="hero-location">📍 México 🇲🇽</p>
          <div className="hero-badges">
            {BADGES.map(ba => <span key={ba} className="hero-badge">{ba}</span>)}
          </div>
          <button className="theme-toggle" onClick={toggleTheme} aria-pressed={!darkMode}>
            {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Header;
